// frontend/src/admin/pages/ReviewQueuePage.jsx
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  listReviewQueue,
  getReviewCase,
  submitCaseReview,
} from "../api";

const AGENTS = ["aria", "dev", "sam", "mara", "promo"];

/** Review actions, mirroring the agent_case_reviews CHECK vocabulary. */
const ACTIONS = [
  { value: "add_few_shot", label: "Add few-shot" },
  { value: "update_kb", label: "Update KB" },
  { value: "add_guardrail", label: "Add guardrail" },
  { value: "edit_prompt", label: "Edit prompt" },
  { value: "no_action", label: "No action" },
];

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString() : "—";
}

/** Five-star CSAT display (filled vs empty); "—" when no score was submitted. */
function CsatStars({ score }) {
  if (!score) return <span className="text-slate-400">—</span>;
  return (
    <span className="text-amber-500" aria-label={`CSAT ${score} of 5`}>
      {"★".repeat(score)}
      <span className="text-slate-300">{"★".repeat(5 - score)}</span>
    </span>
  );
}

/** Small coloured pill for the agent persona. */
function AgentBadge({ agent }) {
  return (
    <span className="inline-block rounded bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">
      {agent}
    </span>
  );
}

/**
 * ReviewQueuePage — operator console for agent cases flagged for human review
 * (P18-G5). Left: the flagged-case table + the action form for the selected
 * case. Right: the full transcript of the selected case. Submitting a review
 * records the verdict and drops the case from the queue.
 */
function ReviewQueuePage({ onReviewed }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState("");

  const [selected, setSelected] = useState(null); // full case detail
  const [detailLoading, setDetailLoading] = useState(false);
  const [action, setAction] = useState(ACTIONS[0].value);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { per_page: 100 };
      if (agent) params.agent = agent;
      const data = await listReviewQueue(params);
      setRows(data.items ?? []);
    } catch {
      toast.error("Failed to load the review queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);

  const openCase = async (caseId) => {
    setDetailLoading(true);
    setAction(ACTIONS[0].value);
    setNotes("");
    try {
      const data = await getReviewCase(caseId);
      setSelected(data);
      // Pre-fill the action with the LLM suggestion (human still clicks Submit).
      const suggested = data.pre_review_suggestion?.suggested_action;
      if (suggested && ACTIONS.some((a) => a.value === suggested)) {
        setAction(suggested);
      }
    } catch {
      toast.error("Failed to load case.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await submitCaseReview(selected.id, { action, notes: notes || null });
      toast.success("Review recorded.");
      setRows((rs) => rs.filter((r) => r.id !== selected.id));
      setSelected(null);
      if (onReviewed) onReviewed();
    } catch {
      toast.error("Failed to submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="mb-5 text-xl font-semibold text-slate-900">
        AI Review Queue
      </h1>

      <label className="mb-4 block w-48">
        <span className="mb-1 block text-xs font-medium text-slate-500">Agent</span>
        <select
          aria-label="Filter by agent"
          value={agent}
          onChange={(e) => setAgent(e.target.value)}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">All agents</option>
          {AGENTS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </label>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left: queue table + action form */}
        <div>
          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500">No cases awaiting review. 🎉</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-slate-500">
                  <th className="py-2 pr-2">Agent</th>
                  <th className="py-2 pr-2">CSAT</th>
                  <th className="py-2 pr-2">Reason</th>
                  <th className="py-2 pr-2">Summary</th>
                  <th className="py-2 pr-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => openCase(c.id)}
                    className={[
                      "cursor-pointer border-b last:border-0 hover:bg-slate-50",
                      selected?.id === c.id ? "bg-indigo-50" : "",
                    ].join(" ")}
                  >
                    <td className="py-2 pr-2">
                      <AgentBadge agent={c.agent} />
                    </td>
                    <td className="py-2 pr-2">
                      <CsatStars score={c.csat_score} />
                    </td>
                    <td className="py-2 pr-2 text-slate-500">{c.flagged_reason}</td>
                    <td className="py-2 pr-2 text-slate-700">
                      {(c.case_summary || "").slice(0, 60)}
                      {(c.case_summary || "").length > 60 ? "…" : ""}
                    </td>
                    <td className="py-2 pr-2 whitespace-nowrap text-slate-500">
                      {fmt(c.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {selected && (
            <div className="mt-6 rounded-lg border border-slate-200 p-4">
              {selected.pre_review_suggestion && (
                <div
                  data-testid="pre-review-tip"
                  className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                >
                  <span className="font-semibold">Suggested action: </span>
                  {selected.pre_review_suggestion.suggested_action}
                  {selected.pre_review_suggestion.reasoning
                    ? ` — ${selected.pre_review_suggestion.reasoning}`
                    : ""}
                </div>
              )}
              <h2 className="mb-3 text-sm font-semibold text-slate-800">
                Review this case
              </h2>
              <label className="mb-3 block">
                <span className="mb-1 block text-xs font-medium text-slate-500">
                  Action
                </span>
                <select
                  aria-label="Review action"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  {ACTIONS.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="mb-3 block">
                <span className="mb-1 block text-xs font-medium text-slate-500">
                  Notes
                </span>
                <textarea
                  aria-label="Review notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Optional notes…"
                />
              </label>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Submit review"}
              </button>
            </div>
          )}
        </div>

        {/* Right: transcript of the selected case */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          {detailLoading ? (
            <p className="text-sm text-slate-500">Loading case…</p>
          ) : !selected ? (
            <p className="text-sm text-slate-400">
              Select a case to view its transcript.
            </p>
          ) : (
            <div>
              <div className="mb-3 flex items-center gap-2">
                <AgentBadge agent={selected.agent} />
                <span className="text-xs text-slate-500">
                  {selected.resolution_type || "—"} · {fmt(selected.created_at)}
                </span>
              </div>
              {selected.case_summary && (
                <p className="mb-3 text-sm text-slate-700">
                  {selected.case_summary}
                </p>
              )}
              <div
                data-testid="transcript"
                className="space-y-2 text-sm"
              >
                {(selected.transcript || []).map((turn, i) => (
                  <div
                    key={i}
                    className={
                      turn.role === "user"
                        ? "text-slate-800"
                        : "text-indigo-700"
                    }
                  >
                    <span className="font-semibold">{turn.role}: </span>
                    <span className="whitespace-pre-wrap">{turn.content}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReviewQueuePage;
